import classNames from 'classnames';
import { useContext } from 'react';
import { Outlet, useLocation } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { ListWithDetailLayout } from '@sorare/core/src/components/layout/ListWithDetailLayout';
import { ANY_SPORT_ESSENCE_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { shardsIconUrl } from '@sorare/core/src/lib/rewards';
import { matchAnySportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useIsCardPullEnabled } from 'hooks/my-cards/useIsCardPullEnabled';

import { MyCardsLayoutContext } from '../MyCardsLayout/MyCardsLayoutContext';

const Root = styled(Vertical)`
  height: calc(
    var(--100vh) - var(--current-stack-height, 0px) - var(
        --bottomsheet-shrinked-height,
        0px
      ) - var(--navbar-height-mobile, 0px)
  );
`;
const OutletContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const Content = styled.div`
  padding: var(--unit) 0;
`;
const GridContainer = styled.div`
  width: min-content;
  margin: auto;
  max-width: 100%;
  padding: var(--half-unit) 0;
  overflow-x: auto;

  @media ${desktopAndAbove} {
    width: unset;
    margin: unset;
  }
`;
const Grid = styled.div`
  display: flex;
  gap: var(--unit);
  padding: 0 var(--double-unit);

  @media ${desktopAndAbove} {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 0;
  }
`;
const StyledLink = styled(Link)`
  aspect-ratio: 1;
  & > * {
    width: 100%;
    aspect-ratio: 1;
  }
`;
const Item = styled(Horizontal).attrs({ gap: 0, center: true })`
  border: 1px solid var(--c-nd-100);
  background: radial-gradient(
    40% 40% at 50% 50%,
    #424242 0%,
    var(--c-black) 100%
  );
  border-radius: var(--unit);
  aspect-ratio: 1;
  position: relative;
  width: calc(10 * var(--unit));

  @media ${desktopAndAbove} {
    width: unset;
  }

  &:hover {
    border-color: var(--c-nd-400);
  }

  &.selected {
    border-color: var(--c-blue-400);
  }

  &.global {
    img {
      filter: grayscale(1);
    }
  }

  img {
    width: 65%;
    aspect-ratio: 1;
    object-fit: contain;

    @media ${desktopAndAbove} {
      width: calc(8 * var(--unit));
    }
  }

  span {
    font: var(--t-body-m);
    line-height: 1;
    position: absolute;
    right: var(--unit);
    bottom: var(--unit);
  }
`;
const StyledBadge = styled(Badge)`
  display: block;
`;

type Props = {
  type: Rarity;
};

export const EssenceLayout = ({ type }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const isLaptopAndAbove = useIsLaptop();
  const { rewardsBannerHeight } = useContext(MyCardsLayoutContext);

  const isDesktopAndAbove = useIsDesktopAndAbove();
  const { pathname } = useLocation();
  const { currentUser } = useCurrentUserContext();

  const { shardsChests } = currentUser || {};

  const blockchainRarities = [
    Rarity.limited,
    Rarity.rare,
    Rarity.super_rare,
    Rarity.unique,
  ] as const;

  const isCardPullEnabled = useIsCardPullEnabled();

  if (isCardPullEnabled) {
    return <Outlet />;
  }

  return (
    <ConditionalWrapper
      Wrapper={ListWithDetailLayout}
      wrap={isDesktopAndAbove}
      props={{ extraStackHeight: rewardsBannerHeight }}
    >
      <Root>
        {!isDesktopAndAbove && (
          <OutletContainer>
            <Outlet />
          </OutletContainer>
        )}
        {!!isLaptopAndAbove && (
          <Content>
            <GridContainer>
              <Grid>
                {blockchainRarities.map(rarity => {
                  const shardsChest = shardsChests?.find(
                    chest => chest.rarity === rarity
                  );

                  return (
                    <StyledLink
                      key={rarity}
                      replace
                      to={generateSportContextPath(ANY_SPORT_ESSENCE_TYPE, {
                        params: {
                          type: rarity,
                        },
                      })}
                      aria-label={`${rarity} essence`}
                    >
                      <StyledBadge
                        variant="dot"
                        invisible={!shardsChest?.newThresholdUnlocked}
                        top={0.5}
                        right={0.5}
                      >
                        <Item
                          className={classNames({
                            selected:
                              matchAnySportPath(
                                ANY_SPORT_ESSENCE_TYPE,
                                pathname
                              ) && rarity === type,
                          })}
                        >
                          <img src={shardsIconUrl(rarity)} alt="" />
                          <span>{shardsChest?.cardShardsCount || 0}</span>
                        </Item>
                      </StyledBadge>
                    </StyledLink>
                  );
                })}
              </Grid>
            </GridContainer>
          </Content>
        )}
      </Root>
    </ConditionalWrapper>
  );
};
